import dayjs from "dayjs";
import { DateTimeValue } from "../types/asset";
import { TIME_SLOTS } from "../constants/autostack";

export const formatDateTime = (date: Date, time: string): string => {
  if (!date || !time) return "";

  return dayjs(date).format("D MMMM YYYY") + `, ${time}`;
};

export const parseDateTime = (value: string): DateTimeValue | null => {
  if (!value || value.includes("Immediately")) {
    return null;
  }

  try {
    const parts = value.split(", ");
    if (parts.length !== 2) return null;

    const [datePart, timePart] = parts;
    const parsedDate = dayjs(datePart);

    if (!parsedDate.isValid()) {
      return null;
    }

    return { date: parsedDate.toDate(), time: timePart };
  } catch (error) {
    console.error("Error parsing date-time:", error);
    return null;
  }
};

export const isValidFutureDate = (date: Date): boolean => {
  if (!date || isNaN(date.getTime())) {
    return false;
  }

  return dayjs(date).isAfter(dayjs());
};

export const calculateMonthStartDate = (
  monthOption: string,
  defaultTime: string = TIME_SLOTS[0]
): string => {
  const now = dayjs();
  let targetDate: dayjs.Dayjs;

  switch (monthOption) {
    case "1st": {
      targetDate = now.add(1, "month").startOf("month");
      break;
    }
    case "15th": {
      const current15th = now.date(15);

      if (now.date() <= 15) {
        targetDate = current15th;
      } else {
        targetDate = now.add(1, "month").date(15);
      }
      break;
    }
    case "last": {
      const lastDayOfCurrentMonth = now.endOf("month");

      if (now.isBefore(lastDayOfCurrentMonth, "day")) {
        targetDate = lastDayOfCurrentMonth;
      } else {
        targetDate = now.add(1, "month").endOf("month");
      }
      break;
    }
    default: {
      targetDate = now.add(1, "month").startOf("month");
      break;
    }
  }

  return formatDateTime(targetDate.toDate(), defaultTime);
};

const DAY_OF_WEEK_MAP: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const calculateWeekStartDate = (
  dayOfWeek: string,
  defaultTime: string = TIME_SLOTS[0]
): string => {
  const now = dayjs();
  const targetDayOfWeek = DAY_OF_WEEK_MAP[dayOfWeek];

  if (targetDayOfWeek === undefined) {
    // Fallback if invalid day
    return calculateWeekStartDate("monday", defaultTime);
  }

  const currentDayOfWeek = now.day();
  const daysUntilTarget = (targetDayOfWeek - currentDayOfWeek + 7) % 7;

  if (daysUntilTarget === 0) {
    // If it's the same day, find the next available time slot
    const nextAvailableTime = findNextAvailableTimeSlot(defaultTime);
    return formatDateTime(now.toDate(), nextAvailableTime);
  } else {
    const targetDate = now.add(daysUntilTarget, "day");
    return formatDateTime(targetDate.toDate(), defaultTime);
  }
};

const isTimeSlotAvailable = (timeSlot: string, now: dayjs.Dayjs): boolean => {
  const [hourStr, minuteStr] = timeSlot.split(":");
  const timeSlotToday = now
    .hour(parseInt(hourStr, 10))
    .minute(parseInt(minuteStr, 10))
    .second(0)
    .millisecond(0);

  return timeSlotToday.isAfter(now);
};

const findNextAvailableTimeSlot = (preferredTime: string): string => {
  const now = dayjs();
  const preferredIndex = TIME_SLOTS.indexOf(preferredTime as any);

  // If preferred time is not found, use the first available slot
  if (preferredIndex === -1) {
    return TIME_SLOTS[0];
  }

  // If preferred time is still available, use it
  if (isTimeSlotAvailable(preferredTime, now)) {
    return preferredTime;
  }

  // Find the next available time slot
  const remainingTimeSlots = TIME_SLOTS.slice(preferredIndex + 1);
  const nextAvailableTimeSlot = remainingTimeSlots.find((timeSlot) =>
    isTimeSlotAvailable(timeSlot, now)
  );

  return nextAvailableTimeSlot ?? TIME_SLOTS[0];
};

export const calculateDayStartDate = (
  selectedTime: string = TIME_SLOTS[0]
): string => {
  const now = dayjs();
  const [hourStr, minuteStr] = selectedTime.split(":");
  const hours = parseInt(hourStr, 10);
  const minutes = parseInt(minuteStr, 10);

  const timeSlotToday = now
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0);

  // If the selected time has already passed today, schedule for tomorrow
  const targetDate = timeSlotToday.isAfter(now)
    ? timeSlotToday
    : now.add(1, "day").hour(hours).minute(minutes).second(0).millisecond(0);

  return formatDateTime(targetDate.toDate(), selectedTime);
};

export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDate = (dateString: string | undefined): Date | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

export const formatDateDisplay = (dateString: string): string => {
  const date = parseDate(dateString);
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Converts local time string to UTC time string
 * @param time - Time in HH:MM format (local timezone)
 * @returns Time in HH:MM format (UTC)
 * @example localTimeToUTC("10:00") returns "08:00" for UTC+2 timezone
 */
export const localTimeToUTC = (time: string): string => {
  const [hours, minutes] = time.split(":").map(Number);

  const localDate = new Date();
  localDate.setHours(hours, minutes, 0, 0);

  const utcHours = localDate.getUTCHours();
  const utcMinutes = localDate.getUTCMinutes();

  return `${utcHours.toString().padStart(2, "0")}:${utcMinutes.toString().padStart(2, "0")}`;
};

/**
 * Converts UTC time string to local time string
 * @param time - Time in HH:MM format (UTC)
 * @returns Time in HH:MM format (local timezone)
 * @example utcTimeToLocal("08:00") returns "10:00" for UTC+2 timezone
 */
export const utcTimeToLocal = (time: string): string => {
  const [hours, minutes] = time.split(":").map(Number);

  const utcDate = new Date();
  utcDate.setUTCHours(hours, minutes, 0, 0);

  const localHours = utcDate.getHours();
  const localMinutes = utcDate.getMinutes();

  return `${localHours.toString().padStart(2, "0")}:${localMinutes.toString().padStart(2, "0")}`;
};
